import { isRoutine } from '../../ui/routines.js';
/**
 * Mindmap View - Layout Module
 *
 * Gap management and node dimension calculations. Tree positioning lives in
 * tidy-layout.js.
 * Uses callback injection pattern for dependencies.
 */

import * as State from './state.js';

// ============================================================
// Constants (moved from index.js)
// ============================================================
export const DEFAULT_NODE_HEIGHT = 50;
export const DEFAULT_GAP_PERCENT = 50;
export const MIN_GAP_PERCENT = 10;
export const MAX_GAP_PERCENT = 300;
export const GAP_STEP = 10;

// ============================================================
// Module callbacks (injected via init)
// ============================================================
let layoutCallbacks = null;

/**
 * Initialize layout module with callbacks
 * @param {Object} cbs
 * @param {Function} cbs.triggerRender - Trigger mindmap render
 */
export function init(cbs) {
  layoutCallbacks = cbs;
}

// ============================================================
// Gap Management
// ============================================================

/**
 * Get the current vertical gap between sibling nodes
 * Returns a fixed gap regardless of node size, so blank space is consistent
 * @returns {number} Gap in pixels
 */
export function getVerticalGap() {
  return Math.round(DEFAULT_NODE_HEIGHT * (State.gapPercent / 100));
}

/**
 * Get extra gap to add between sibling subtrees (non-leaf nodes)
 * This helps visually distinguish blocks of children belonging to different siblings
 * @returns {number} Extra gap in pixels (50% of standard node height)
 */
export function getSiblingSubtreeGap() {
  return Math.round(DEFAULT_NODE_HEIGHT * 0.5);
}

/**
 * Set the vertical gap percentage
 * @param {number} percent - Gap as percentage of node height
 */
export function setGapPercent(percent) {
  State.setGapPercent(Math.max(MIN_GAP_PERCENT, Math.min(MAX_GAP_PERCENT, percent)));
  updateGapDisplay();
  // Persist to Store
  if (State.callbacks && State.callbacks.onMindmapGapChange) {
    State.callbacks.onMindmapGapChange(State.gapPercent);
  }
  // Re-render: the layout reads the gap on every render.
  if (State.container && State.callbacks && State.currentState) {
    layoutCallbacks?.triggerRender?.();
  }
}

/**
 * Increase the vertical gap
 */
export function increaseGap() {
  setGapPercent(State.gapPercent + GAP_STEP);
}

/**
 * Decrease the vertical gap
 */
export function decreaseGap() {
  setGapPercent(State.gapPercent - GAP_STEP);
}

/**
 * Update the gap percentage display in the UI
 */
export function updateGapDisplay() {
  const display = State.container?.querySelector("#m-gap-display");
  if (display) {
    display.textContent = `${State.gapPercent}%`;
  }
}

// ============================================================
// Text Measurement & Node Dimensions
// ============================================================

/**
 * Get canvas context for text measurement (lazy init, reused)
 */
export function getMeasurementCtx() {
  if (!State.measurementCtx) {
    State.setMeasurementCanvas(document.createElement('canvas'));
    State.setMeasurementCtx(State.measurementCanvas.getContext('2d'));
  }
  return State.measurementCtx;
}

/**
 * Calculate node dimensions based on text content
 * Rules:
 * - Max 1000 characters
 * - Grow horizontally up to 4x default, then grow vertically
 * - Target 80 chars per line max
 * - Target width:height ratio of ~3:1
 * @param {string} text - The node text
 * @param {boolean} isRoot - Whether this is the root node
 * @param {boolean} isLeaf - Whether this is a leaf node
 * @returns {{width: number|null, height: number|null, lines: number}}
 */
// Memo for calculateNodeDimensions: layout calls it from several passes
// (weights, positioning, rendering) for the same unchanged text, and canvas
// measureText is the hot part. Keyed by type+text; cleared when it grows past
// a bound so pathological documents can't leak memory. The bound is above
// the largest maps (about 27,000 items at the 10 MB limit): at 4,000, a map
// with more names than that emptied it on every render and measured all again.
const _dimensionsMemo = new Map();
const DIMENSIONS_MEMO_MAX = 60000;

export function calculateNodeDimensions(text, isRoot, isLeaf, nodeId=null) {
  if(nodeId&&isRoutine(nodeId)) {
    const dims=calculateNodeDimensions(text,isRoot,isLeaf);
    const ctx=getMeasurementCtx();ctx.font=`600 ${isRoot?16:isLeaf?12:13}px system-ui`;
    return {...dims,width:Math.ceil((dims.width||Math.min(220,ctx.measureText(text||'').width)+44)*1.5),height:Math.ceil((dims.height||DEFAULT_NODE_HEIGHT)*1.6)};
  }
  const memoKey = (isRoot ? 'r' : isLeaf ? 'l' : 'b') + '|' + (text || '');
  const memoized = _dimensionsMemo.get(memoKey);
  if (memoized) return memoized;

  const result = _calculateNodeDimensions(text, isRoot, isLeaf);
  if (_dimensionsMemo.size >= DIMENSIONS_MEMO_MAX) _dimensionsMemo.clear();
  _dimensionsMemo.set(memoKey, result);
  return result;
}

function _calculateNodeDimensions(text, isRoot, isLeaf) {
  const trimmedText = (text || '').slice(0, 1000);
  const charCount = trimmedText.length;

  // Font settings per node type (from CSS)
  const fontSize = isRoot ? 16 : (isLeaf ? 12 : 13);
  const fontWeight = isRoot ? 700 : (isLeaf ? 500 : 600);
  const fontFamily = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  // Padding values from CSS
  const paddingX = isRoot ? 30 : (isLeaf ? 18 : 22);
  const paddingY = isRoot ? 14 : (isLeaf ? 8 : 10);

  // Default content width and max (4x default)
  const defaultContentWidth = isRoot ? 180 : (isLeaf ? 120 : 150);
  const maxContentWidth = defaultContentWidth * 4;
  const maxCharsPerLine = 80;

  const nodeType = isRoot ? 'root' : (isLeaf ? 'leaf' : 'branch');

  // For very short text, use natural sizing
  if (charCount <= 15) {
    if (!State.suppressDimensionLogs) {
      console.log(`[Mindmap Dims] "${trimmedText.slice(0, 30)}..." (${charCount} chars, ${nodeType}) -> natural sizing (short text)`);
    }
    return { width: null, height: null, lines: 1 };
  }

  // Measure actual text width using canvas
  const ctx = getMeasurementCtx();
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  const fullTextWidth = ctx.measureText(trimmedText).width;

  // Line height for multi-line text
  const lineHeight = fontSize * 1.5;

  // If text fits in default width, use natural sizing
  if (fullTextWidth <= defaultContentWidth) {
    if (!State.suppressDimensionLogs) {
      console.log(`[Mindmap Dims] "${trimmedText.slice(0, 30)}..." (${charCount} chars, ${nodeType}) -> natural sizing (fits default ${defaultContentWidth}px, measured ${Math.round(fullTextWidth)}px)`);
    }
    return { width: null, height: null, lines: 1 };
  }

  // Calculate optimal layout
  // Try to achieve ~3:1 width:height ratio while respecting constraints
  let contentWidth, numLines;
  let layoutReason = '';

  if (fullTextWidth <= maxContentWidth) {
    // Text fits in single line within 4x max
    contentWidth = fullTextWidth;
    numLines = 1;
    layoutReason = `single line (${Math.round(fullTextWidth)}px <= ${maxContentWidth}px max)`;
  } else {
    // Need multi-line layout
    // Calculate chars per line based on max width
    const avgCharWidth = fullTextWidth / charCount;
    const charsPerMaxWidth = Math.floor(maxContentWidth / avgCharWidth);
    const effectiveCharsPerLine = Math.min(charsPerMaxWidth, maxCharsPerLine);

    // Calculate number of lines needed
    numLines = Math.ceil(charCount / effectiveCharsPerLine);

    // For 3:1 ratio, try to balance width and height
    // width = 3 * height => contentWidth = 3 * (numLines * lineHeight)
    const idealWidthFor3to1 = 3 * numLines * lineHeight;

    // Use the smaller of: max width, ideal 3:1 width, or full text width
    if (numLines === 1) {
      contentWidth = Math.min(fullTextWidth, maxContentWidth);
      layoutReason = `clamped single line`;
    } else {
      // For multi-line, recalculate to balance
      // Try to find a width that gives us a good ratio
      contentWidth = Math.min(maxContentWidth, Math.max(idealWidthFor3to1, defaultContentWidth));

      // Recalculate lines based on this width
      const charsForWidth = Math.floor(contentWidth / avgCharWidth);
      numLines = Math.ceil(charCount / Math.min(charsForWidth, maxCharsPerLine));
      layoutReason = `multi-line (${effectiveCharsPerLine} chars/line, ideal3:1=${Math.round(idealWidthFor3to1)}px)`;
    }
  }

  // Calculate content height
  const contentHeight = numLines * lineHeight;

  // Total dimensions including padding
  const width = Math.round(contentWidth + paddingX * 2);
  const height = Math.round(contentHeight + paddingY * 2);

  // Minimum dimensions
  const minWidth = isRoot ? 100 : (isLeaf ? 60 : 80);
  const minHeight = isRoot ? 48 : (isLeaf ? 32 : 40);

  const finalWidth = Math.max(width, minWidth);
  const finalHeight = Math.max(height, minHeight);
  const ratio = (finalWidth / finalHeight).toFixed(1);

  if (!State.suppressDimensionLogs) {
    console.log(`[Mindmap Dims] "${trimmedText.slice(0, 30)}${trimmedText.length > 30 ? '...' : ''}" (${charCount} chars, ${nodeType})`);
    console.log(`  -> ${layoutReason}`);
    console.log(`  -> content: ${Math.round(contentWidth)}x${Math.round(contentHeight)}px, padding: ${paddingX}x${paddingY}px`);
    console.log(`  -> final: ${finalWidth}x${finalHeight}px (${numLines} lines, ratio ${ratio}:1)`);
  }

  return {
    width: finalWidth,
    height: finalHeight,
    lines: numLines
  };
}
